import React, { useCallback, useState } from 'react';
import { useTranslation } from '@shared/core';
import { useMediaQuery } from '@withjoy/joykit/utils';
import { Flex, TextV2, Box, BoxProps } from '@withjoy/joykit';
import { useLayout } from '@apps/guest/packages/layout-engine/layouts/LayoutProvider';
import { AccommodationCombined, AccommodationCombinedType, AccommodationSourceType } from '../Accommodations/Accommodation.types';
import { pxToRem } from '@withjoy/joykit/theme';
import { createDateFromUnformattedString } from '@shared/utils/dateHelpers';
import { SkeletonText, SkeletonThumbnail } from '@shared/components/Skeleton';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import { TextEllipsisWrapper } from '@shared/utils/formatting/textEllipsis';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { GuestSiteTypographyOverride } from '@apps/guest/components/GuestSiteTypographyOverride/GuestSiteTypographyOverride';
import {
  hed2ToSubHeadingOverride,
  label3ToParagraphOverride,
  body1ToParagraphOverride,
} from '@apps/guest/components/GuestSiteTypographyOverride/GuestSiteTypographyOverride.constants';
import { useScaleGuestSiteFontSize } from '@apps/guest/components/GuestSiteTypographyOverride/hooks/useScaleGuestSiteFontSize';
import { Category } from '@graphql/generated';
import { useAccommodationsCta } from '../../hooks/useAccommodationsCta';
import { DEFAULT_HOTEL_IMAGE, DEFAULT_HOTEL_IMAGE_WIDE } from '@shared/utils/constants';
import { EventVenue } from '@shared/utils/eventPlace';
import { isInIframe } from '@shared/utils/isInIframe';
import { useTextLineClampOverflow } from '@shared/hooks/useTextLineClampOverflow';
import { JoyPlacePricing } from './components/JoyPlacePricing';
import { JoyPlacePricingV2 } from './components/JoyPlacePricingV2';
import { RoomBlockPricingNonGrid } from './components/RoomBlockPricingNonGrid';
import { animationTransition } from '@shared/utils/animationTransition';
import { CTAButton } from './components/CTAButton';
import { ReservationsCount } from './components/ReservationsCount';
import { VenueAndTravelDistance } from './components/VenueAndTravelDistance';
import { PhotoCarousel } from '@shared/components/PhotoCarousel';
import { wrapPartnerHotelRedirect } from '../WrapPartnerHotelRedirect';
import { StarRating } from '@shared/components';
import { ACCOMMODATIONS_NOTE_TEXT_CLASS_NAME } from '@apps/guest/packages/layout-engine/layouts/layout.constants';
import { COMPARISON_PRICE_MIN_HEIGHT } from './AccommodationsTileV2.constants';
import { AccommodationsExternalHotelLinkInterceptor } from './components/AccommodationsExternalHotelLinkInterceptor';
import { useHotelDiscount } from '@shared/components/DiscountsProvider/hotels/HotelsDiscountsProvider';
import { DiscountCouponBadge } from '@apps/stays/components/DiscountCouponBadge';
import { createSearchParamsFromAccommodationsPage } from '@apps/guest/routes/HotelBooking/HotelBooking.routes';

interface AccommodationsTileV2FavoriteProps
  extends Readonly<{
    locationInfo?: EventVenue;
    isFirst: boolean;
    isLast: boolean;
    showDefaultData?: boolean;
    reservationCount: number;
    isInterceptEnabled: boolean;
    accommodation: AccommodationCombined;
    accommodationPropertyPhotos?: { url: string }[];
    setLocationBeingHovered?: (id: string | undefined) => void;
    onSelectAccommodation?: (event: React.MouseEvent<HTMLAnchorElement>) => void;
    from?: AccommodationSourceType;
    onAccommodationsConsoleOpen: () => void;
    isAccommodationLinkInterceptionEnabled: boolean;
    enableHoverShadow?: boolean;
    isFullWidthLayout?: boolean;
  }> {}

const AccommodationsTileV2Favorite = wrapPartnerHotelRedirect((props: AccommodationsTileV2FavoriteProps) => {
  const {
    locationInfo,
    setLocationBeingHovered,
    accommodation,
    reservationCount,
    accommodationPropertyPhotos,
    onSelectAccommodation,
    onAccommodationsConsoleOpen,
    isInterceptEnabled,
    isAccommodationLinkInterceptionEnabled,
    enableHoverShadow,
    isFullWidthLayout,
  } = props;
  const { displayName, description, photo, cutoffDate, pricePerNight, id, interestedGuestCount, type, distance, distances, starRating } = accommodation;

  const { layout } = useLayout();
  const { t } = useTranslation('guestSiteCustom');
  const accTrans = t('accommodations');
  const isMobile = useMediaQuery(theme => theme.mediaQueries.between(0, { breakpointAlias: 'sm2' }));
  const [isAccommodationsExpandedMapEnabled] = useIsFeatureEnabled('accommodationsExpandedMap');
  const isMobileOrTablet = useMediaQuery(theme => theme.mediaQueries.between(0, { breakpointAlias: 'md' }));
  const inPreviewMode = isInIframe();
  const joyHotelDiscount = useHotelDiscount();

  const isBrannan = layout === 'brannan';
  const isGridView = !isAccommodationsExpandedMapEnabled && isBrannan && !isMobile;

  const [showInterceptModal, setShowInterceptModal] = useState(false);

  const isRoomBlock = type === AccommodationCombinedType.Roomblock;
  const { textRef, isOverflowing } = useTextLineClampOverflow(isRoomBlock ? 1 : 2);
  const [isCoupleNoteOpen, setIsCoupleNoteOpen] = useState(false);

  const { ctaTitle, ctaUrl, ctaVariant, isLinkEnabled, isShowButton, onSoldOutItemClick } = useAccommodationsCta({
    accommodation,
    locationInfo,
    from: AccommodationSourceType.HotelMapList,
    searchParams: createSearchParamsFromAccommodationsPage('accommodations_tile_favorite'),
    onSoldOutItemClickAction: onAccommodationsConsoleOpen,
  });

  const [showPricingEnabled] = useIsFeatureEnabled('travelShowHotelPrice');

  const showRegularHotelPricing = !accommodation.customUrl && showPricingEnabled && !inPreviewMode;

  const [showNewPriceComparisonUI] = useIsFeatureEnabled('enableAccommodationsPriceComparisonV2');
  const [accommodationGuestHotelTileFontUpdatesEnabled] = useIsFeatureEnabled('accommodationGuestHotelTileFontUpdates');

  const hasPrice = (type === AccommodationCombinedType.Hotel && showRegularHotelPricing) || (type === AccommodationCombinedType.Roomblock && pricePerNight);

  const handleCoupleNoteOpen = useEventCallback(e => {
    e.preventDefault();
    e.stopPropagation();
    setIsCoupleNoteOpen(isOpen => !isOpen);
  });

  const handleOnAccommodationClick = useEventCallback(e => {
    if (
      isInterceptEnabled &&
      isAccommodationLinkInterceptionEnabled &&
      accommodation.joyPlaceId &&
      accommodation.isCustomLinkEnabled &&
      accommodation.type === AccommodationCombinedType.Custom
    ) {
      e.preventDefault();
      e.stopPropagation();

      setShowInterceptModal(true);
      return;
    } else {
      onSelectAccommodation?.(e);
    }
  });

  const handleInterceptClose = useEventCallback(() => {
    setShowInterceptModal(false);
  });

  const [fontPackExperimentEnabled] = useIsFeatureEnabled('fontPackExperiment');
  const { scaleFontSize } = useScaleGuestSiteFontSize();

  const renderTagComponent = useCallback(() => {
    if (reservationCount) {
      return <ReservationsCount reservationsCount={reservationCount} />;
    } else if (interestedGuestCount && interestedGuestCount > 1) {
      return (
        <Flex
          borderRadius={pxToRem(20.632)}
          height={pxToRem(32)}
          alignItems="center"
          paddingX={4}
          backgroundColor="rgba(255, 255, 255, 0.5)"
          backdropFilter="blur(4px)"
        >
          {accommodationGuestHotelTileFontUpdatesEnabled ? (
            <TextV2
              fontFamily={'Inter UI'}
              color={'black'}
              fontSize={pxToRem(13)}
              letterSpacing={pxToRem(-0.032)}
              fontWeight={600}
              alignSelf={'center'}
              typographyVariant="label3"
            >
              {accTrans.guestsInterested({ interestedGuestCount })}
            </TextV2>
          ) : (
            <GuestSiteTypographyOverride
              override={label3ToParagraphOverride}
              defaultFontFamily="Inter UI"
            >
              <TextV2
                {...(!fontPackExperimentEnabled && { fontFamily: 'Inter UI' })}
                fontSize={scaleFontSize(pxToRem(13), Category.PARAGRAPH)}
                letterSpacing={pxToRem(-0.032)}
                fontWeight={600}
                alignSelf={'center'}
                color={'black'}
              >
                {accTrans.guestsInterested({ interestedGuestCount })}
              </TextV2>
            </GuestSiteTypographyOverride>
          )}
        </Flex>
      );
    }

    return null;
  }, [reservationCount, interestedGuestCount, accTrans, accommodationGuestHotelTileFontUpdatesEnabled, fontPackExperimentEnabled, scaleFontSize]);

  return (
    <>
      <Flex
        position="relative"
        width={'100%'}
        className="group"
        overflow="hidden"
        flexDirection="column"
        backgroundColor="white"
        borderRadius={pxToRem(12)}
        onClick={onSoldOutItemClick}
        {...(isAccommodationsExpandedMapEnabled && !isMobile && isBrannan && { flex: '1 1 calc(33.33% - 10px)', backgroundColor: 'white' })}
        {...(isLinkEnabled && { as: 'a', href: ctaUrl, onClick: handleOnAccommodationClick, target: '_blank' })}
        cursor={isShowButton ? 'pointer' : 'default'}
        _hover={{
          cursor: isShowButton ? 'pointer' : 'default',
          transform: isShowButton && !isMobile ? 'translateY(-2px)' : 'none',
          ...(enableHoverShadow && {
            boxShadow: isShowButton && !isMobile ? '0px 8px 32px 0px rgba(0, 0, 0, 0.12), 0px 2px 8px 0px rgba(0, 0, 0, 0.08)' : '0px 4px 16px 0px rgba(0, 0, 0, 0.08)',
          }),
        }}
        boxShadow="0px 2px 8px 0px rgba(0, 0, 0, 0.06), 0px 1px 3px 0px rgba(0, 0, 0, 0.04)"
        transition={animationTransition('boxShadow', 'transform', {
          duration: '300ms',
        })}
        onMouseEnter={() => {
          if (!isMobileOrTablet) {
            setLocationBeingHovered?.(id);
          }
        }}
        onMouseLeave={() => {
          if (!isMobileOrTablet) {
            setLocationBeingHovered?.(undefined);
          }
        }}
      >
        {/* Carousel - no padding, extends to edges */}
        <Box
          width={'100%'}
          position="relative"
        >
          <Flex
            justifyContent="space-between"
            position="absolute"
            top={4}
            left={4}
            right={4}
            zIndex={1}
            flexWrap="wrap"
            gap={4}
          >
            <Box
              display={'flex'}
              justifyContent="center"
              alignItems="center"
              columnGap={2}
              width={32}
              height={32}
              borderRadius="50%"
              backdropFilter={'blur(4px)'}
              backgroundColor={`rgba(247, 247, 247, 0.6)`}
            >
              <TextV2
                typographyVariant="label3"
                fontWeight={500}
                fontFamily="Inter UI"
                color="mono14"
              >
                1
              </TextV2>
            </Box>
            {
              <Box
                display="flex"
                alignItems="center"
                justifyContent={'flex-end'}
                gap={1}
              >
                {renderTagComponent()}
                <DiscountCouponBadge
                  discount={joyHotelDiscount}
                  accommodationType={accommodation.type}
                />
              </Box>
            }
          </Flex>
          <Box
            width="100%"
            aspectRatio="3/2"
            overflow={'hidden'}
            position={'relative'}
            backgroundColor={'mono2'}
            maxHeight={pxToRem(isGridView ? 324 : 206)}
          >
            <PhotoCarousel
              aspectRatio="3/2"
              images={
                accommodationPropertyPhotos?.map(photo => ({ src: photo?.url, alt: displayName || '' })) ?? [
                  { src: photo?.url || (isMobile ? DEFAULT_HOTEL_IMAGE : DEFAULT_HOTEL_IMAGE_WIDE), alt: displayName || '' },
                ]
              }
              navigation={{ variant: 'minimal', visible: 'default' }}
            />
          </Box>
        </Box>

        <Box
          gap={3}
          width="100%"
          paddingX={isFullWidthLayout ? 5 : pxToRem(32)}
          paddingY={isFullWidthLayout ? 5 : pxToRem(24)}
          display="flex"
          flexDirection="column"
        >
          <Flex
            gap={3}
            flexDirection={isGridView ? 'row' : 'column'}
            justifyContent={{ _: 'center', sm2: isBrannan ? 'space-between' : 'center' }}
          >
            <Flex
              gap={7}
              flex={1}
              justifyContent={{ _: 'center', sm2: isBrannan && isAccommodationsExpandedMapEnabled ? 'space-between' : 'center' }}
              flexDirection="column"
            >
              <Flex
                gap={4}
                flexDirection="column"
              >
                <Flex
                  gap={1}
                  flexDirection="column"
                >
                  {accommodationGuestHotelTileFontUpdatesEnabled ? (
                    <TextEllipsisWrapper
                      typographyVariant="hed2"
                      lineClamp={2}
                      fontFamily="Inter UI"
                      fontSize={pxToRem(22)}
                      fontWeight={600}
                      color="mono14"
                      lineHeight={pxToRem(28.6)}
                      letterSpacing={'-0.528px'}
                      title={displayName || ''}
                    >
                      {displayName}
                    </TextEllipsisWrapper>
                  ) : (
                    <GuestSiteTypographyOverride
                      override={hed2ToSubHeadingOverride}
                      defaultFontFamily="Inter UI"
                    >
                      <TextEllipsisWrapper
                        lineClamp={2}
                        {...(!fontPackExperimentEnabled && { fontFamily: 'Inter UI' })}
                        fontSize={scaleFontSize(pxToRem(22), Category.SUB_HEADING)}
                        fontWeight={600}
                        color="mono14"
                        lineHeight={pxToRem(28.6)}
                        letterSpacing={'-0.528px'}
                        title={displayName || ''}
                      >
                        {displayName}
                      </TextEllipsisWrapper>
                    </GuestSiteTypographyOverride>
                  )}
                  <Flex
                    gap={{ _: 1, sm2: 2 }}
                    alignItems="center"
                    flexWrap="wrap"
                  >
                    {!!starRating && (
                      <StarRating
                        rating={starRating}
                        id={accommodation.id}
                        hideNonFilledStars
                      />
                    )}
                    <VenueAndTravelDistance
                      distances={distances}
                      milesToVenue={distance}
                      useShortWordingDistance={true}
                    />
                  </Flex>
                  <Flex
                    gap={9}
                    width="100%"
                    justifyContent="space-between"
                    alignItems={isCoupleNoteOpen ? 'flex-start' : 'flex-end'}
                  >
                    {(description || (cutoffDate && isGridView)) && (
                      <Box>
                        {description && (
                          <Flex
                            width="100%"
                            alignItems="flex-end"
                          >
                            <TextEllipsisWrapper
                              ref={textRef}
                              lineClamp={isCoupleNoteOpen ? 'none' : isRoomBlock ? 1 : 2}
                              lineHeight={1.5}
                              className={ACCOMMODATIONS_NOTE_TEXT_CLASS_NAME}
                            >
                              {accommodationGuestHotelTileFontUpdatesEnabled ? (
                                <TextV2
                                  typographyVariant="body1"
                                  fontWeight={400}
                                  color="mono10"
                                  title={description}
                                  lineHeight={1.5}
                                >
                                  {description}
                                </TextV2>
                              ) : (
                                <GuestSiteTypographyOverride
                                  override={body1ToParagraphOverride}
                                  defaultFontFamily="Inter UI"
                                >
                                  <TextV2
                                    {...(!fontPackExperimentEnabled && { fontFamily: 'Inter UI' })}
                                    fontSize={scaleFontSize(pxToRem(15), Category.PARAGRAPH)}
                                    fontWeight={400}
                                    color="mono10"
                                    title={description}
                                    lineHeight={1.5}
                                  >
                                    {description}
                                  </TextV2>
                                </GuestSiteTypographyOverride>
                              )}
                            </TextEllipsisWrapper>
                            {isOverflowing &&
                              (accommodationGuestHotelTileFontUpdatesEnabled ? (
                                <TextV2
                                  fontWeight={400}
                                  _hover={{ cursor: 'pointer' }}
                                  color="mono10"
                                  textDecoration={'underline'}
                                  onClick={handleCoupleNoteOpen}
                                  typographyVariant="body1"
                                  fontFamily="Inter UI"
                                  lineHeight={1.5}
                                >
                                  {isCoupleNoteOpen ? `${accTrans.less()}` : `${accTrans.more()}`}
                                </TextV2>
                              ) : (
                                <GuestSiteTypographyOverride
                                  override={body1ToParagraphOverride}
                                  defaultFontFamily="Inter UI"
                                >
                                  <TextV2
                                    {...(!fontPackExperimentEnabled && { fontFamily: 'Inter UI' })}
                                    fontSize={scaleFontSize(pxToRem(15), Category.PARAGRAPH)}
                                    fontWeight={400}
                                    _hover={{ cursor: 'pointer' }}
                                    color="mono10"
                                    onClick={handleCoupleNoteOpen}
                                    lineHeight={1.5}
                                    textDecoration={'underline'}
                                  >
                                    {isCoupleNoteOpen ? `${accTrans.less()}` : `${accTrans.more()}`}
                                  </TextV2>
                                </GuestSiteTypographyOverride>
                              ))}
                          </Flex>
                        )}

                        {cutoffDate && isGridView && (
                          <TextV2
                            typographyVariant={'body1'}
                            width="100%"
                            fontWeight={400}
                            color="accentGreen3"
                            lineHeight={1.5}
                            letterSpacing="-0.15px"
                          >
                            {accTrans.beforeDate({
                              date: createDateFromUnformattedString(cutoffDate)?.toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' }),
                            })}
                          </TextV2>
                        )}
                      </Box>
                    )}
                    {isGridView && hasPrice && (
                      <Box
                        width="100%"
                        minHeight={pxToRem(COMPARISON_PRICE_MIN_HEIGHT)}
                      >
                        {/* PRICING FOR HOTEL (ENABLED WITH EXPERIMENT TREATMENT) */}
                        {type === AccommodationCombinedType.Hotel && showRegularHotelPricing ? (
                          showNewPriceComparisonUI ? (
                            <JoyPlacePricingV2
                              width={description ? '50%' : '100%'}
                              minWidth={pxToRem(288)}
                              accommodation={accommodation}
                              locationInfo={locationInfo}
                              isGridView={true}
                            />
                          ) : (
                            <JoyPlacePricing
                              width={description ? '50%' : '100%'}
                              minWidth={pxToRem(288)}
                              accommodation={accommodation}
                              isGridView={true}
                              isFromTiles={true}
                            />
                          )
                        ) : null}
                        {/* BEGIN ROOM BLOCK FOR GRID VIEW, IS ON THE RIGHT SIDE OF DETAILS */}
                        {type === AccommodationCombinedType.Roomblock && pricePerNight && (
                          <RoomBlockPricingForGrid
                            width={description || cutoffDate ? '50%' : '100%'}
                            minWidth="162px"
                            accommodation={accommodation}
                          />
                        )}
                      </Box>
                    )}
                    {/* END ROOM BLOCK FOR GRID VIEW */}
                  </Flex>
                </Flex>
                {!isGridView && hasPrice && (
                  <Box
                    width="100%"
                    minHeight={pxToRem(COMPARISON_PRICE_MIN_HEIGHT)}
                  >
                    {/* BEGIN ROOM BLOCK PRICING */}
                    {type === AccommodationCombinedType.Roomblock && pricePerNight ? <RoomBlockPricingNonGrid accommodation={accommodation} /> : null}
                    {/* END ROOM BLOCK PRICING */}

                    {/* PRICING FOR HOTEL (ENABLED WITH EXPERIMENT TREATMENT) */}
                    {type === AccommodationCombinedType.Hotel && showRegularHotelPricing ? (
                      showNewPriceComparisonUI ? (
                        <JoyPlacePricingV2
                          width={'100%'}
                          accommodation={accommodation}
                          locationInfo={locationInfo}
                          isGridView={isGridView}
                        />
                      ) : (
                        <JoyPlacePricing
                          width={'100%'}
                          accommodation={accommodation}
                          isGridView={isGridView}
                          isLargeTile={!isMobile}
                          isFromTiles={true}
                        />
                      )
                    ) : null}
                  </Box>
                )}
              </Flex>
            </Flex>
          </Flex>

          {isShowButton && (
            <CTAButton
              ctaTitle={ctaTitle}
              variant={ctaVariant}
              marginTop={pxToRem(32)}
              width={isMobile ? { _: '100%', sm2: isBrannan && isAccommodationsExpandedMapEnabled ? pxToRem(278) : '100%' } : '100%'}
            />
          )}
        </Box>
      </Flex>
      <AccommodationsExternalHotelLinkInterceptor
        isOpen={showInterceptModal}
        accommodation={accommodation}
        onClose={handleInterceptClose}
        onSelectAccommodation={onSelectAccommodation}
      />
    </>
  );
});

const AccommodationsTileV2FavoriteSkeleton: React.FC = () => {
  const { layout } = useLayout();
  const isBrannan = layout === 'brannan';
  const isMobile = useMediaQuery(theme => theme.mediaQueries.between(0, { breakpointAlias: 'sm2' }));
  const [isAccommodationsExpandedMapEnabled] = useIsFeatureEnabled('accommodationsExpandedMap');
  const isGridView = isBrannan && !isMobile && !isAccommodationsExpandedMapEnabled;

  return (
    <Flex
      width={'100%'}
      overflow="hidden"
      backgroundColor="white"
      borderRadius={pxToRem(12)}
      boxShadow="0px 2px 8px 0px rgba(0, 0, 0, 0.06), 0px 1px 3px 0px rgba(0, 0, 0, 0.04)"
      flexDirection="column"
    >
      {/* Image - no padding, extends to edges */}
      <SkeletonThumbnail
        height={pxToRem(isGridView ? 324 : 206)}
        overrides={{
          PlaceholderRoot: {
            props: {
              width: '100%',
              backgroundColor: 'white',
            },
          },
        }}
      />

      <Box
        gap={3}
        paddingX={4}
        paddingY={6}
        display="flex"
        flexDirection="column"
      >
        <Flex
          gap={3}
          flexDirection={isGridView ? 'row' : 'column'}
        >
          <Flex
            gap={4}
            flex={1}
            flexDirection="column"
          >
            {/* Hotel name */}
            <SkeletonText
              rows={1}
              width="70%"
              skeletonHeight={pxToRem(24)}
            />

            {/* Distance */}
            <SkeletonText
              rows={1}
              width="40%"
              skeletonHeight={pxToRem(16)}
            />

            {/* Description */}
            <Box>
              <SkeletonText
                rows={1}
                width="100%"
                skeletonHeight={pxToRem(16)}
                paddingBottom={2}
              />
              <SkeletonText
                rows={1}
                width="80%"
                skeletonHeight={pxToRem(16)}
              />
            </Box>

            {/* Pricing for non-grid */}
            {!isGridView && (
              <Flex
                justifyContent="space-between"
                alignItems="center"
              >
                <SkeletonText
                  rows={1}
                  width="30%"
                  skeletonHeight={pxToRem(16)}
                />
                <SkeletonText
                  rows={1}
                  width="25%"
                  skeletonHeight={pxToRem(20)}
                />
              </Flex>
            )}

            {/* Button for non-grid */}
            {!isGridView && (
              <SkeletonText
                rows={1}
                width="100%"
                skeletonHeight={pxToRem(44)}
              />
            )}
          </Flex>

          {/* Pricing for grid view */}
          {isGridView && (
            <Flex
              flexDirection="column"
              gap={2}
              minWidth="172px"
              alignItems="flex-end"
            >
              <SkeletonText
                rows={1}
                width="80%"
                skeletonHeight={pxToRem(16)}
              />
              <SkeletonText
                rows={1}
                width="100%"
                skeletonHeight={pxToRem(20)}
              />
            </Flex>
          )}
        </Flex>
      </Box>
    </Flex>
  );
};

export { AccommodationsTileV2Favorite, AccommodationsTileV2FavoriteSkeleton };

const RoomBlockPricingForGrid = (props: { accommodation: AccommodationCombined } & BoxProps) => {
  const { accommodation, ...boxProps } = props;
  const { strikeoutPricePerNight, pricePerNight } = accommodation;
  const accTrans = useTranslation('guestSiteCustom').t('accommodations');
  return (
    <Flex
      flexDirection="column"
      gap={1}
      {...boxProps}
    >
      <TextV2
        typographyVariant={'body1'}
        color="accentGreen3"
        fontWeight={400}
        textAlign="right"
      >
        {accTrans.groupRate()}
      </TextV2>
      <Flex
        gap={2}
        alignItems="center"
        justifyContent="flex-end"
      >
        {!!strikeoutPricePerNight && (
          <TextV2
            color="mono10"
            fontSize={pxToRem(17)}
            letterSpacing={'-0.002px'}
            typographyVariant="body3"
            fontWeight={400}
            backgroundSize={'100% 1.5px'}
            width={'auto'}
            backgroundImage={'linear-gradient(currentColor, currentColor)'}
            backgroundPosition={'0% 50%'}
            backgroundRepeat={'no-repeat'}
            lineHeight={1.6}
          >
            ${strikeoutPricePerNight}
          </TextV2>
        )}
        <Box
          display={'flex'}
          typographyVariant={'hed4'}
          alignItems={'center'}
          justifyContent={'flex-end'}
          color="mono14"
          fontWeight={600}
          letterSpacing={'-0.02em'}
          textTransform="none"
          lineHeight={1.3}
        >
          ${accTrans.pricePerNight({ price: pricePerNight })}
        </Box>
      </Flex>
    </Flex>
  );
};
